// @flow
import * as React from 'react';

import clsx from 'clsx';

import { Layer } from '@gandi/ui';

import { ReshapedGandiProvider } from 'src/Reshaped/ReshapedGandiProvider';

import { useIsMobile, useFocusLoop } from '../../hooks';

import { HeaderMenuContext } from '../LandingPagesControls/HeaderMenuContext';

import { HeaderContent } from './HeaderContent';
import { HeaderContext } from './HeaderContext';
import { ThemeToggle } from './ThemeToggle';

import styles from './Header.scss';

export type Props = {
  controls?: React.Node,
  widgets?: React.Node,
  className?: string,
};

function Header({ controls, widgets, className }: Props): React.MixedElement {
  const headerRef = React.useRef<?React.ElementRef<'header'>>(null);
  const portalContainerRef = React.useRef<?HTMLDivElement>(null);
  const burgerRef = React.useRef<?HTMLButtonElement>(null);

  const [mainMenuOpen, setMainMenuOpen] = React.useState(false);
  const isMobile = useIsMobile();

  useFocusLoop(mainMenuOpen, portalContainerRef);

  const contextValue = React.useMemo(
    () => ({ mainMenuOpen, setMainMenuOpen, burgerRef }),
    [mainMenuOpen, setMainMenuOpen, burgerRef],
  );

  const headerContent = (
    <header
      id="main-header"
      role="banner"
      ref={headerRef}
      className={clsx(styles.root, className)}
    >
      <HeaderContent>
        {controls}
        {/* $FlowFixMe[sketchy-number-and] */}
        {widgets && <div className={styles.widgets}>{widgets}</div>}
      </HeaderContent>
      {/* Temporary button used for quick switch testing; to be improved later */}
      <ThemeToggle />
    </header>
  );

  return (
    <HeaderContext.Provider value={headerRef}>
      <HeaderMenuContext.Provider
        // $FlowFixMe[incompatible-type]
        value={contextValue}
      >
        {isMobile ? (
          <Layer>
            <ReshapedGandiProvider>
              <div
                {...(isMobile && mainMenuOpen
                  ? {
                      role: 'dialog',
                      'aria-modal': 'true',
                      className: styles.open,
                    }
                  : {})}
                ref={portalContainerRef}
              >
                {headerContent}
              </div>
            </ReshapedGandiProvider>
          </Layer>
        ) : (
          headerContent
        )}
      </HeaderMenuContext.Provider>
    </HeaderContext.Provider>
  );
}

export { Header };
